import React, { useCallback, useMemo } from 'react';
import { compose } from 'redux';
import { KeyboardAwareScrollView } from 'react-native-keyboard-aware-scroll-view';
import { View, RefreshControl } from 'react-native';
import moment from 'moment';
import Toast from 'react-native-root-toast';
import { useMutation } from '@apollo/client';

import { DetailContainer } from '../../../containers';
import { useRefreshPage } from '../../../hooks/useRefreshPage';
import { useThemedStyles } from '../../../hooks/useThemedStyles';
import { WarningCallout, Spinner } from '../../../components';
import { StatusBadge } from '../../../design/components';
import PageHeading from '../../../design/components/PageHeading';
import SetupSection from '../components/SetupSection';
import SetupInfoRow from '../components/SetupInfoRow';
import SetupSwitchField from '../components/SetupSwitchField';
import SetupPhotoSection from '../components/SetupPhotoSection';
import SetupMoveStatus from '../components/SetupMoveStatus';

import { flattenObjectDataInArray } from '../../../../common/utils/general/flattenObjectData';
import { getProductHealthComplianceLocalLabel } from '../../../../common/utils/products/healthCompliance';
import { useUserContext } from '../../../../common/utils/context';
import { useOfflineMutation } from '../../../offline';
import { CREATE_MEDIA, MODIFY_MEDIA } from '../../../../common/graphql/media';
import { MODIFY_COMM_DEVICE } from '../../../../common/graphql/commDevice';
import { getMachineSetupCardPaymentOnlyDisplay } from '../../../../common/utils/machine/machineSetupPayments';

import {
	MACHINE_DOOR_ANGLE_NAME,
	STATUS,
	YES_NO_VALUE_NAME,
} from '../../../../common/constants';
import { getStatusLabel } from '../../../utils/statusLabel';
import MachineSetupInformationSection from './MachineSetupInformationSection';
import MachineSetupPhysicalSection from './MachineSetupPhysicalSection';
import { createMachineSetupDetailStyles } from './MachineSetupDetail.styles';

const MachineSetupDetail = ({
	data: machineSetup,
	modifyItem,
	refetch,
}) => {
	const styles = useThemedStyles(createMachineSetupDetailStyles);
	const [refreshPage, isRefreshing] = useRefreshPage(refetch);
	const { tenant, isDriver: userIsDriver, isSuperAdmin } = useUserContext();
	const isDriver = Boolean(userIsDriver && !isSuperAdmin);
	const [createMedia] = useOfflineMutation(CREATE_MEDIA, { configKey: 'createMedia' });
	const [modifyMedia] = useMutation(MODIFY_MEDIA);
	const [modifyCommDevice] = useMutation(MODIFY_COMM_DEVICE);

	const healthComplianceLabel = getProductHealthComplianceLocalLabel(tenant, true);
	const safeMachineSetup = useMemo(() => machineSetup || {}, [machineSetup]);
	const assignedMachine = safeMachineSetup.machine || safeMachineSetup.plannedMachine;
	const cardPaymentOnlyDisplay = getMachineSetupCardPaymentOnlyDisplay(safeMachineSetup);

	const updateMachineSetup = useCallback((values) => {
		return modifyItem(values);
	}, [modifyItem]);

	const addPhoto = useCallback(async ({ link, targetField }) => {
		const result = await createMedia({
			variables: {
				input: {
					machine: safeMachineSetup.machine?.id,
					machineSetup: safeMachineSetup.id,
					link,
				},
			},
		});

		const newMedia = result?.data?.createMedia;
		const newId = newMedia?.id;

		if (!newId) {
			Toast.show('Unable to upload photo');
			return;
		}

		const currentIds = flattenObjectDataInArray(safeMachineSetup[targetField] || []);
		await updateMachineSetup({ [targetField]: [...currentIds, newId] });

		if (result?.offline) {
			Toast.show('Photo queued; will sync when online');
		}
		else {
			Toast.show('Photo uploaded');
		}
	}, [createMedia, safeMachineSetup, updateMachineSetup]);

	const handleUpdateMediaComment = useCallback(async (media, comment) => {
		try {
			await modifyMedia({
				variables: {
					input: {
						id: media.id,
						comment: comment || null,
					},
				},
			});
			Toast.show('Note saved');
		}
		catch (error) {
			Toast.show('Unable to save note');
		}
	}, [modifyMedia]);

	const handlePaymentDeviceSerialUpdate = useCallback(async ({ paymentDeviceSerialNo }) => {
		const value = paymentDeviceSerialNo;
		if (!isDriver && safeMachineSetup.commDevice?.id) {
			try {
				await modifyCommDevice({
					variables: {
						input: {
							id: safeMachineSetup.commDevice.id,
							setupInformation: {
								...(safeMachineSetup.commDevice.setupInformation || {}),
								paymentDeviceSerialNo: value,
							},
						},
					},
				});
			}
			catch (error) {
				Toast.show('Unable to update comm device');
			}
		}
		return updateMachineSetup({ paymentDeviceSerialNo: value });
	}, [isDriver, safeMachineSetup.commDevice, modifyCommDevice, updateMachineSetup]);

	const dueDateLabel = safeMachineSetup.locationSetup?.dueDate ? moment(safeMachineSetup.locationSetup.dueDate).format('DD MMM YYYY') : 'Not set';
	const statusLabel = getStatusLabel(safeMachineSetup.status);

	if (!machineSetup) {
		return (
			<View style={styles.loaderContainer}>
				<Spinner />
			</View>
		);
	}

	return (
		<KeyboardAwareScrollView
			style={styles.scrollView}
			scrollIndicatorInsets={{ right: 1 }}
			refreshControl={(
				<RefreshControl
					refreshing={isRefreshing}
					onRefresh={refreshPage}
				/>
			)}
		>
			<View style={styles.content}>
				<View style={styles.headingBlock}>
					<PageHeading title={safeMachineSetup.identifier} subtitle={dueDateLabel} />
					{statusLabel && (
						<StatusBadge
							label={statusLabel}
							tone={machineSetup.status === STATUS.COMPLETE ? 'success' : 'neutral'}
							size="small"
							style={styles.statusBadge}
						/>
					)}
				</View>

				<SetupMoveStatus pointOfSaleLabel="Machine" setup={safeMachineSetup} />

				{
					!isDriver && machineSetup.status === STATUS.POST_SETUP_ADMIN_COMPLETE && (
						<WarningCallout
							text="Post setup admin is complete. You can now book transport in the location setup."
						/>
					)
				}

				{isDriver && (
					<SetupSection title="Setup details">
						<SetupInfoRow
							label="Identifier"
							value={safeMachineSetup.identifier}
						/>
						<SetupInfoRow
							label="Due date"
							value={dueDateLabel}
						/>
						<SetupInfoRow
							label="Location"
							value={safeMachineSetup.locationSetup?.location?.name}
						/>
						<SetupInfoRow
							label="Place"
							value={safeMachineSetup.place?.name || 'Not set'}
						/>
						<SetupInfoRow
							label="Machine"
							value={assignedMachine?.assetId || 'Not set'}
						/>
						<SetupInfoRow
							label="Route"
							value={safeMachineSetup.route?.name || 'Not set'}
						/>
						<SetupInfoRow
							label="Required planogram"
							value={safeMachineSetup.planogram?.name || 'Not set'}
						/>
						<SetupInfoRow
							label="Card payment only machine?"
							value={cardPaymentOnlyDisplay}
						/>
						<SetupInfoRow
							label="Required door angle"
							value={MACHINE_DOOR_ANGLE_NAME[safeMachineSetup.requiredDoorAngle] || 'Not set'}
						/>
						<SetupInfoRow
							label="Requires extension lead"
							value={YES_NO_VALUE_NAME[safeMachineSetup.requiresExtensionLead] || 'Not set'}
						/>
						{/* Drivers get the plain-language label; admins keep the HCG/HFD terminology used on the web forms. */}
						<SetupInfoRow
							label="Health compliance guidelines met"
							value={safeMachineSetup.healthCompliant ? 'Yes' : 'No'}
						/>
						<SetupInfoRow
							label="Additional instructions"
							value={safeMachineSetup.additionalInstructions || 'None'}
						/>
						<SetupPhotoSection
							title="Site photos"
							photos={safeMachineSetup.placePhotos || []}
						/>
					</SetupSection>
				)}

				{!isDriver && (
					<MachineSetupInformationSection
						machineSetup={machineSetup}
						assignedMachine={assignedMachine}
						cardPaymentOnlyDisplay={cardPaymentOnlyDisplay}
						dueDateLabel={dueDateLabel}
						healthComplianceLabel={healthComplianceLabel}
						updateMachineSetup={updateMachineSetup}
						addPhoto={addPhoto}
						handleUpdateMediaComment={handleUpdateMediaComment}
					/>
				)}

				<MachineSetupPhysicalSection
					machineSetup={machineSetup}
					assignedMachine={assignedMachine}
					isDriver={isDriver}
					cardPaymentOnlyDisplay={cardPaymentOnlyDisplay}
					updateMachineSetup={updateMachineSetup}
					addPhoto={addPhoto}
					handleUpdateMediaComment={handleUpdateMediaComment}
					handlePaymentDeviceSerialUpdate={handlePaymentDeviceSerialUpdate}
				/>

				{!isDriver && machineSetup.requiresCreditCardReader === true && (
					<SetupSection title="Post setup admin">
						<SetupSwitchField
							label="Verify that credit card transactions are received by us"
							value={machineSetup.verifyCreditCardTransaction}
							onChange={value => updateMachineSetup({ verifyCreditCardTransaction: value })}
							required={true}
							hint="Check Payline and verify that funds are received from test transactions on this machine."
						/>
					</SetupSection>
				)}
			</View>
		</KeyboardAwareScrollView>
	);
};

export default compose(DetailContainer)(MachineSetupDetail);
